import { Component } from '@theme/component';
import { debounce, onDocumentLoaded, setHeaderMenuStyle } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu - The overflow menu.
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  /**
   * @type {MutationObserver | null}
   */
  #submenuMutationObserver = null;

  /**
   * Timeout ID for the delayed close, so diagonal mouse movement between
   * sibling menu items doesn't instantly close the open submenu.
   * @type {number | null}
   */
  #deactivateTimeout = null;

  /**
   * Timeout ID for the delayed switch to a newly-hovered item, so quickly
   * passing over a sibling item (on the way to a submenu, or just passing
   * through) doesn't instantly close whatever submenu is currently open.
   * @type {number | null}
   */
  #activateTimeout = null;

  /**
   * How long (ms) to wait before closing the submenu after the pointer leaves.
   * @type {number}
   */
  #closeDelay = 450;

  /**
   * How long (ms) the pointer must dwell on a new item, while another item's
   * submenu is already open, before switching to it.
   * @type {number}
   */
  #openDelay = 150;

  connectedCallback() {
    super.connectedCallback();

    this.overflowMenu?.addEventListener('pointerleave', () => this.#deactivate());
    // on load, cache the max height of the submenu so you can use it in a translate
    this.#cacheMaxOverflowMenuHeight();

    onDocumentLoaded(this.#preloadImages);
    window.addEventListener('resize', this.#resizeListener);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.#resizeListener);
    this.#cleanupMutationObserver();
    this.#clearDeactivateTimeout();
    this.#clearActivateTimeout();
  }

  /**
   * Debounced resize event listener to recalculate menu style
   */
  #resizeListener = debounce(() => {
    this.#cacheMaxOverflowMenuHeight();
    setHeaderMenuStyle();
  }, 100);

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
  };

  /**
   * Get the overflow menu
   */
  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]'));
  }

  /**
   * Whether the overflow menu is hovered
   * @returns {boolean}
   */
  get overflowHovered() {
    return this.refs.overflowMenu?.matches(':hover') ?? false;
  }

  get headerComponent() {
    return /** @type {HTMLElement | null} */ (this.closest('header-component'));
  }

  /**
   * Cancel any pending delayed close.
   */
  #clearDeactivateTimeout() {
    if (this.#deactivateTimeout !== null) {
      clearTimeout(this.#deactivateTimeout);
      this.#deactivateTimeout = null;
    }
  }

  /**
   * Cancel any pending delayed switch to a newly-hovered item.
   */
  #clearActivateTimeout() {
    if (this.#activateTimeout !== null) {
      clearTimeout(this.#activateTimeout);
      this.#activateTimeout = null;
    }
  }

  /**
   * Handle pointerenter/focus on a menu item. If nothing is currently open,
   * activates immediately for a responsive first hover. If another item's
   * submenu is already open, debounces the switch briefly so quickly
   * passing over a sibling item (e.g. on the way down to an open submenu)
   * doesn't instantly close it.
   * @param {PointerEvent | FocusEvent} event
   */
  activate = (event) => {
    this.dispatchEvent(new MegaMenuHoverEvent());

    if (!(event.target instanceof Element)) return;

    const item = findMenuItem(event.target);

    if (!item) return;

    if (item === this.#state.activeItem) {
      // Still on the already-active item — cancel any pending close/switch.
      this.#clearDeactivateTimeout();
      this.#clearActivateTimeout();
      return;
    }

    // A different item is being entered, so cancel any pending close from
    // a previous pointerleave and any previously-queued switch.
    this.#clearDeactivateTimeout();
    this.#clearActivateTimeout();

    if (this.#state.activeItem) {
      // Something is already open — wait briefly before switching, so a
      // quick pass over this item doesn't instantly close the current one.
      this.#activateTimeout = window.setTimeout(() => {
        this.#activateTimeout = null;
        this.#performActivate(item, event);
      }, this.#openDelay);
    } else {
      // Nothing open yet — activate right away.
      this.#performActivate(item, event);
    }
  };

  /**
   * Actually perform the activation of a menu item (open its submenu,
   * close the previous one).
   * @param {HTMLElement} item
   * @param {PointerEvent | FocusEvent} event
   */
  #performActivate = (item, event) => {
    if (!(event.target instanceof Element)) return;

    const isDefaultSlot = event.target.slot === '';

    this.dataset.overflowExpanded = (!isDefaultSlot).toString();

    const previouslyActiveItem = this.#state.activeItem;

    if (previouslyActiveItem) {
      previouslyActiveItem.ariaExpanded = 'false';
    }

    this.#state.activeItem = item;
    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';

    let submenu = findSubmenu(item);

    if (!submenu && !isDefaultSlot) {
      submenu = this.overflowMenu;
    }

    if (submenu) {
      // Mark submenu as active for content-visibility optimization
      submenu.dataset.active = '';

      // Cleanup any existing mutation observer from previous menu activations
      this.#cleanupMutationObserver();

      // Monitor DOM mutations to catch deferred content injection (from section hydration)
      this.#submenuMutationObserver = new MutationObserver(() => {
        requestAnimationFrame(() => {
          // Double requestAnimationFrame to ensure the height is properly calculated and not defaulting to the contain-intrinsic-size
          requestAnimationFrame(() => {
            if (submenu.offsetHeight > 0) {
              this.headerComponent?.style.setProperty('--submenu-height', `${submenu.offsetHeight}px`);
              this.#cleanupMutationObserver();
            }
          });
        });
      });
      this.#submenuMutationObserver.observe(submenu, {childList: true, subtree: true});

      // Auto-disconnect after 500ms to prevent memory leaks
      setTimeout(() => {
        this.#cleanupMutationObserver();
      }, 500);
    }

    const submenuHeight = submenu ? submenu.offsetHeight : 0;

    this.headerComponent?.style.setProperty('--submenu-height', `${submenuHeight}px`);
    this.style.setProperty('--submenu-opacity', '1');
  };

  /**
   * Deactivate the active item after a delay, so moving diagonally across
   * a sibling menu item (e.g. Bath -> Best Sellers) on the way down to the
   * open submenu doesn't prematurely close it.
   * @param {PointerEvent | FocusEvent} event
   */
  deactivate(event) {
    if (!(event.target instanceof Element)) return;

    // If the pointer leaves this item before its debounced activation had
    // a chance to fire, cancel that pending switch — the user was just
    // passing through.
    this.#clearActivateTimeout();

    const menu = findSubmenu(this.#state.activeItem);
    const isMovingWithinMenu = event.relatedTarget instanceof Node && menu?.contains(document.activeElement);
    const isMovingToSubmenu =
      event.relatedTarget instanceof Node && event.type === 'blur' && menu?.contains(event.relatedTarget);
    const isMovingToOverflowMenu =
      event.relatedTarget instanceof Node && event.relatedTarget.parentElement?.matches('[slot="overflow"]');

    if (isMovingWithinMenu || isMovingToOverflowMenu || isMovingToSubmenu) return;

    // Don't close immediately — give the user time to reach the submenu
    // (or another top-level item) before collapsing it.
    this.#clearDeactivateTimeout();
    const itemAtTimeOfLeave = this.#state.activeItem;
    this.#deactivateTimeout = window.setTimeout(() => {
      this.#deactivateTimeout = null;
      this.#deactivate(itemAtTimeOfLeave);
    }, this.#closeDelay);
  }

  /**
   * Deactivate the active item immediately
   * @param {HTMLElement | null} [item]
   */
  #deactivate = (item = this.#state.activeItem) => {
    if (!item || item != this.#state.activeItem) return;
    if (this.overflowHovered) return;

    this.headerComponent?.style.setProperty('--submenu-height', '0px');
    this.style.setProperty('--submenu-opacity', '0');
    this.dataset.overflowExpanded = 'false';

    const submenu = findSubmenu(item);

    this.#state.activeItem = null;
    this.ariaExpanded = 'false';
    item.ariaExpanded = 'false';

    // Remove active state from submenu after animation completes
    if (submenu) {
      delete submenu.dataset.active;
    }
  };

  /**
   * Preload images that are set to load lazily.
   */
  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image) => image.removeAttribute('loading'));
  };

  /**
   * Caches the maximum height of all submenus for consistent animations
   * Stores the value in a CSS custom property for use in transitions
   */
  #cacheMaxOverflowMenuHeight() {
    const submenus = this.querySelectorAll('[ref="submenu[]"]');
    const maxHeight = Math.max(
      ...Array.from(submenus)
        .filter((submenu) => submenu instanceof HTMLElement)
        .map((submenu) => submenu.offsetHeight)
    );
    this.headerComponent?.style.setProperty('--submenu-max-height', `${maxHeight}px`);
  }

  #cleanupMutationObserver() {
    this.#submenuMutationObserver?.disconnect();
    this.#submenuMutationObserver = null;
  }
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * Find the closest menu item.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  if (element?.matches('[slot="more"')) {
    // Select the first overflowing menu item when hovering over the "More" item
    return findMenuItem(element.parentElement?.querySelector('[slot="overflow"]'));
  }

  return element?.querySelector('[ref="menuitem"]');
}

/**
 * Find the closest submenu.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}